import { useDisclosure } from '@wegroup/design-system';
import { useState } from 'react';
import { getPartyById } from '../../../../common/src/legacy/services/apiRouterService';
import useSetQueryParam from '../../../../common/src/hooks/query-params/useAddQueryParam';
import { useCompanyScanSelector } from '../../../../common/src/utils/globalVariables';
import { shouldEnrich } from '../../../../parties/src/features/party-company-extended-detail/utils';
import { useCreatePartyFromCompanyRegistration } from '../api/createPartyFromCompanyRegistration';
import { useUpdatePartyForCompanyData } from '../api/updatePartyForCompanyData';
import { CompanyModals } from '../types';
import { hasCompanyScanCompanyCBE } from '../utils';
import { ErrorResponse } from '../../../../../types/API';
import { CompanyRegistration, Party } from '../../../../parties/src/types';
import { usePostParty } from '../../../../parties/src/features/creation/api/postParty';

interface CompanyInit {
  isOpen: boolean;
  onClose: () => void;
  currentModal?: CompanyModals;
  checkCompanyRegNr: () => Promise<boolean>;
  checkPid: (handleOnFinish: () => void) => Promise<boolean>;
  checkEnrichement: (handleOnFinish: () => void) => Promise<boolean>;
  isLoading: boolean;
}

const useCompanyInit = (partyId?: string): CompanyInit => {
  const companyRegistration = useCompanyScanSelector(
    (state) => state.companyScan.selectedCompany?.company_registration,
  );
  const companyName = useCompanyScanSelector(
    (state) => state.companyScan.companyName,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentModal, setCurrentModal] = useState<CompanyModals | undefined>(
    undefined,
  );

  const {
    mutateAsync: createPartyFromCompanyRegistration,
    isLoading: isLoadingCompReg,
  } = useCreatePartyFromCompanyRegistration();
  const { mutateAsync: createProspect, isLoading: isLoadingProspCreate } =
    usePostParty();
  const { mutateAsync: updatePartyForCompanyData, isLoading: isUpdatingParty } =
    useUpdatePartyForCompanyData();

  const addQueryParam = useSetQueryParam();

  const checkCompanyRegNr = async () => {
    onClose();
    const hasCbe = await hasCompanyScanCompanyCBE({ partyId });
    if (!hasCbe && !companyName) {
      setCurrentModal(CompanyModals.NO_CBE);
      onOpen();
      return true;
    }

    return false;
  };

  const checkPid = async (handleOnFinish: () => void) => {
    if (!partyId) {
      let resp;
      if (companyRegistration?.nr && companyRegistration?.type) {
        resp = await createPartyFromCompanyRegistration(
          companyRegistration as CompanyRegistration,
        );
      } else {
        resp = await createProspect({
          type: 'COMPANY',
          payload: {
            name: companyName,
          },
        });
      }

      addQueryParam({
        party_id: resp.id,
      });

      handleOnFinish();
      return true;
    }

    return false;
  };

  const checkEnrichement = async (handleOnFinish: () => void) => {
    onClose();
    const [resp] = await getPartyById(partyId);
    const party = resp as Party;

    try {
      // Check if party was ever enriched
      // If FALSE - Enrich party
      if (party.enterprise_details_enriched_at == null) {
        await updatePartyForCompanyData(resp.id);
      }
      // If it was ever enriched but outdated
      // If TRUE - Show modal for enrichment
      else if (shouldEnrich(new Date(party.enterprise_details_enriched_at))) {
        setCurrentModal(CompanyModals.NEEDS_ENRICHEMENT);
        onOpen();
        return true;
      }

      // If function gets to this position, start flow
      handleOnFinish();
    } catch (err) {
      const errMessage = (err as { message?: string })?.message;
      const errObject: ErrorResponse = errMessage ? JSON.parse(errMessage) : {};
      // If company cbe number is not valid
      if (errObject.rpc_code === 27028) {
        setCurrentModal(CompanyModals.WRONG_CBE);
        onOpen();
      }
    }
    return false;
  };

  return {
    isOpen,
    onClose,
    currentModal,
    checkCompanyRegNr,
    checkPid,
    checkEnrichement,
    isLoading: isLoadingCompReg || isLoadingProspCreate || isUpdatingParty,
  };
};

export default useCompanyInit;
